"use client";

import React, { useEffect, useState } from "react";
import { Modal, CircularProgress, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { load } from "@cashfreepayments/cashfree-js";
import OrderConfirmation from "./OrderConfirmation";


const CashfreePaymentModal = ({
  open,
  onClose,
  userID,
  amount,
  shippingID,
  coupon,
  walletAmount,
  shippingAmount,
  credit,
  items,
  cart,
  selectedCoupon,
  useWalletAmount,
  setUseWalletAmount,
  isDoorToDoorChecked,
  freeShipping,
  userDetail,
}) => {
  const [stage, setStage] = useState("loading"); // loading | confirming | confirmed | failed
  const [orderNo, setOrderNo] = useState(null); // LinkID
  const [cashfree, setCashfree] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    load({ mode: process.env.REACT_APP_CASHFREE_MODE })
      .then((sdk) => setCashfree(sdk))
      .catch(() => toast.error("Failed to load Cashfree SDK"));
  }, []);

  useEffect(() => {
    if (open && userID) createPaymentOrder();
  }, [open]);

  /** 🧾 STEP 1: Create Payment Order */
  const createPaymentOrder = async () => {
    try {
      setStage("loading");

      const payload = {
        userID,
        amount: amount < 2 ? 0 : amount,
        shippingID,
        Coupoun_Name: coupon?.couponCode || null,
        Coupoun_Amount: coupon?.couponDiscountTaken || null,
        Wallet_Amount: walletAmount || 0,
        Shipping_Amount: shippingAmount || 0,
        Credit: credit || null,
        items,
      };


      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      // 🧠 Case 1: If payment session ID exists → open gateway
      if (data?.Payment_Session_ID) {
        const linkId = data?.LinkID;
        const sessionId = data?.Payment_Session_ID;
        const orderNo = data?.Order_no
          ? data?.Order_no
          : linkId?.substring(5);

        setOrderNo(orderNo);
        openCashfreeGateway(sessionId, linkId);
        return;
      }

      // 🧠 Case 2: If sessionId missing but order and message exist → show confirmation
      if (!data?.Payment_Session_ID && data?.Order_no && data?.LinkID && data?.message) {
        setOrderNo(data?.Order_no);
        toast.success("Order placed successfully!");
        setStage("confirming");

        setTimeout(() => {
          setStage("confirmed");
        }, 800);

        return;
      }

      // 🧠 Case 3: If no valid data received
      throw new Error(data?.message || "Failed to initialize payment");
    } catch (error) {
      toast.error(error?.message || "Payment initialization failed");
      setStage("failed");
    }
  };

  /** 💳 STEP 2: Open Cashfree Gateway */
  const openCashfreeGateway = async (sessionId, linkId) => {
    try {
      await cashfree.checkout({
        paymentSessionId: sessionId,
        redirectTarget: "_modal",
      });

      // ✅ Do NOT depend on result?.paymentDetails
      // Immediately start polling the backend for final payment status
      startPollingPaymentStatus(linkId);
    } catch (error) {
      setStage("failed");
    }
  };

  /** 🔄 STEP 3: Poll backend every 3s to check payment status */
  const startPollingPaymentStatus = (linkId) => {
    const startTime = Date.now();
    const maxDuration = 15 * 60 * 1000; // 15 minutes in ms
    const intervalTime = 3000; // 3 seconds

    const intervalId = setInterval(async () => {
      const elapsed = Date.now() - startTime;
     

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}payment/dummy/status/${linkId}`
        );
        const data = await res.json();

        // ✅ Backend structure: { status: true, payment: ['FAILED', 'SUCCESS'] }
        if (data?.Status && Array.isArray(data?.payment)) {
          const hasSuccess = data?.payment?.includes("SUCCESS");

          if (hasSuccess) {
            clearInterval(intervalId);
            setPollingInterval(null);

            toast.success("Payment confirmed successfully!");
            setStage("confirming");

            // Optional small delay for smoother transition UX
            setTimeout(() => {
              setStage("confirmed");
            }, 1000);

            return; // ✅ Stop here — do not continue polling
          }
        }

        // Stop after 15 minutes (timeout)
        if (elapsed >= maxDuration) {
          clearInterval(intervalId);
          setPollingInterval(null);
          toast.error("Payment verification timed out after 15 minutes.");
          setStage("failed");
        }
      } catch (err) {
        console.error("❌ Error checking payment status:", err);
        // Do NOT clear interval on transient errors — allow retry in next cycle
      }
    }, intervalTime);

    setPollingInterval(intervalId);
  };

  /** ✖️ Close Modal & Reset */
  const handleClose = () => {
    if (pollingInterval) clearInterval(pollingInterval);
    setPollingInterval(null);
    setStage("loading");
    onClose();
  };

  /** 🧱 Shared fixed-size container */
  const LoaderContainer = ({ children }) => (
    <div
      className="
        bg-white-1 p-8 rounded-lg shadow-md 
        flex flex-col justify-center items-center
        min-w-[380px] min-h-[220px] sm:min-w-[420px] sm:min-h-[240px]
        transition-all duration-300 ease-in-out
      "
    >
      {children}
    </div>
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="flex justify-center items-center h-screen w-full px-3">
        {/* ⏳ Stage: Initializing */}
        {stage === "loading" && (
          <LoaderContainer>
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2, fontSize: "15px", color: "black" }}>
              Please Wait...
            </Typography>
          </LoaderContainer>
        )}

        {/* ✅ Stage: Confirming */}
        {stage === "confirming" && (
          <LoaderContainer>
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2, fontSize: "15px", color: "black" }}>
              Fetching Status...
            </Typography>
          </LoaderContainer>
        )}

        {/* ✅ Stage: Confirmed */}
        {stage === "confirmed" && (
          <OrderConfirmation
            handleClose={handleClose}
            orderNo={orderNo}
            cart={cart}
            selectedCoupon={selectedCoupon}
            useWalletAmount={useWalletAmount}
            setUseWalletAmount={setUseWalletAmount}
            isDoorToDoorChecked={isDoorToDoorChecked}
            freeShipping={freeShipping}
            userDetail={userDetail}
          />
        )}

        {/* ❌ Stage: Failed */}
        {stage === "failed" && (
          <LoaderContainer>
            <Typography
              color="error"
              sx={{ mb: 2, fontSize: "15px", color: "black" }}
            >
              ❌ Payment or Order Failed
            </Typography>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-800 text-white-1 rounded"
            >
              Try Again
            </button>
          </LoaderContainer>
        )}
      </div>
    </Modal>
  );
};

export default CashfreePaymentModal;
